(() => {
  'use strict';
  const NfcUidMath = (() => {
    const widths = [4, 7, 10];
    const fail = key => { throw new Error(key); };
    function convert(raw, format, bytes, padded = false) {
      if (!widths.includes(bytes)) fail('length');
      const text = String(raw).trim();
      if (!text) fail('empty_error');
      const maximum = (1n << BigInt(bytes * 8)) - 1n;
      let value, hex;
      if (format === 'hex') {
        if (!/^[0-9a-f]+$/i.test(text)) fail('hex_error');
        if (text.length !== bytes * 2) fail('hex_length_error');
        hex = text.toUpperCase();
        value = BigInt('0x' + hex);
      } else if (format === 'decimal') {
        if (!/^[0-9]+$/.test(text)) fail('decimal_error');
        const significant = text.replace(/^0+/, '') || '0';
        if (significant.length > maximum.toString().length) fail('overflow_error');
        value = BigInt(significant);
        if (value > maximum) fail('overflow_error');
        hex = value.toString(16).toUpperCase().padStart(bytes * 2, '0');
      } else fail('format');
      const pairs = hex.match(/.{2}/g);
      const reversed = [...pairs].reverse();
      const display = n => padded && bytes === 4 ? n.toString().padStart(10, '0') : n.toString();
      return {
        entered: {hex, bytes: pairs.join(' '), decimal: display(value)},
        reversed: {hex: reversed.join(''), bytes: reversed.join(' '), decimal: display(BigInt('0x' + reversed.join('')))},
        length: bytes
      };
    }
    return {convert};
  })();
  class NfcUidConverter extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true; this.single = null; this.list = null; this.generation = {single: 0, list: 0};
      this.$ = name => this.querySelector('[data-uid="' + name + '"]');
      this.querySelectorAll('[data-uid-listbox]').forEach(listbox => {
        const input = this.$(listbox.dataset.uidListbox);
        listbox.addEventListener('listbox:change', event => {
          input.value = event.detail.value;
          input.dispatchEvent(new Event('change', {bubbles: true}));
        });
      });
      this.messages = JSON.parse(this.querySelector('[data-uid-messages]').textContent);
      this.$('singleForm').addEventListener('submit', event => { event.preventDefault(); this.convertSingle(true); });
      this.$('listForm').addEventListener('submit', event => { event.preventDefault(); this.convertList(true); });
      this.$('raw').addEventListener('input', () => this.invalidate('single'));
      this.$('paste').addEventListener('input', () => this.invalidate('list'));
      this.$('format').addEventListener('change', () => {
        if (this.single) this.$('raw').value = this.single.entered[this.$('format').value];
        this.invalidate('single'); this.invalidate('list'); this.updateFormat();
      });
      this.$('length').addEventListener('change', () => {
        this.invalidate('single'); this.invalidate('list'); this.updateFormat();
      });
      for (const name of ['order', 'output']) this.$(name).addEventListener('change', () => {
        this.updateFormat(); this.render('single'); this.render('list');
      });
      this.$('padding').addEventListener('change', () => {
        if (this.single) this.convertSingle(false);
        if (this.list) this.convertList(false);
      });
      this.querySelectorAll('[data-uid-copy]').forEach(button => button.addEventListener('click', () => this.copy(button.dataset.uidCopy)));
      this.$('download').addEventListener('click', () => this.download());
      this.updateFormat(); this.convertSingle(false);
    }
    disconnectedCallback() {
      if (!this.initialized) return;
      this.invalidate('single'); this.invalidate('list'); this.$('paste').value = '';
    }
    settings() {
      return {format: this.$('format').value, bytes: Number(this.$('length').value), padded: this.$('padding').checked};
    }
    updateFormat() {
      const {format, bytes} = this.settings(), decimal = format === 'decimal';
      this.$('raw').inputMode = decimal ? 'numeric' : 'text';
      this.$('raw').placeholder = decimal ? '305419896' : '12345678'.padStart(bytes * 2, '0');
      this.$('paste').placeholder = decimal ? '305419896\n1\n2712847316' : ['12345678', '00000001', 'A1B2C3D4'].map(value => value.padStart(bytes * 2, '0')).join('\n');
      this.$('formatHint').textContent = decimal ? 'Whole decimal digits (0–9).' : (bytes * 2) + ' HEX characters; no spaces or 0x prefix.';
      this.$('padding').disabled = bytes !== 4 || this.$('output').value !== 'decimal';
      if (bytes !== 4) this.$('padding').checked = false;
    }
    invalidate(kind) {
      this[kind] = null; this.generation[kind]++;
      this.$(kind + 'Results').hidden = true; this.$(kind + 'Result').value = '';
      if (kind === 'single') this.$('byteOrderValue').value = '';
      this.$(kind + 'Error').hidden = true; this.status(kind, '');
      this.$(kind === 'single' ? 'raw' : 'paste').removeAttribute('aria-invalid');
      if (kind === 'list') { this.$('lineErrors').replaceChildren(); this.$('listSummary').textContent = ''; }
    }
    status(kind, text) { const node = this.$(kind + 'Status'); node.textContent = text; node.hidden = !text; }
    error(kind, text, focus) {
      this.$(kind + 'Error').textContent = text; this.$(kind + 'Error').hidden = false;
      const field = this.$(kind === 'single' ? 'raw' : 'paste'); field.setAttribute('aria-invalid', 'true'); if (focus) field.focus();
    }
    convertSingle(focus) {
      this.invalidate('single'); const {format, bytes, padded} = this.settings();
      try { this.single = NfcUidMath.convert(this.$('raw').value, format, bytes, padded); this.render('single'); }
      catch (error) { this.error('single', this.messages[error.message] || error.message, focus); }
    }
    convertList(focus) {
      this.invalidate('list'); const {format, bytes, padded} = this.settings(), text = this.$('paste').value;
      if (!text.trim()) { this.error('list', 'Paste at least one UID.', focus); return; }
      if (text.length > 5 * 1024 * 1024) { this.error('list', 'The list is too large. Paste up to 10,000 UIDs.', focus); return; }
      const lines = text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n').split('\n');
      if (lines.at(-1) === '') lines.pop();
      if (lines.length > 10000) { this.error('list', 'Paste up to 10,000 UIDs.', focus); return; }
      this.list = lines.map((raw, index) => {
        try { return {line: index + 1, value: NfcUidMath.convert(raw, format, bytes, padded)}; }
        catch (error) { return {line: index + 1, error: this.messages[error.message] || error.message}; }
      });
      this.render('list');
      if (this.list.some(row => row.error)) { this.$('paste').setAttribute('aria-invalid', 'true'); if (focus) this.$('paste').focus(); }
    }
    render(kind) {
      this.generation[kind]++; this.status(kind, '');
      if (!this[kind]) return;
      const order = this.$('order').value, output = this.$('output').value;
      const value = result => {
        let text = result[order][output];
        if (output === 'decimal' && result.length === 4 && this.$('padding').checked) text = text.padStart(10, '0');
        return text;
      };
      if (kind === 'single') {
        this.$('byteOrderValue').value = this.single[order].bytes;
        this.$('singleResult').value = value(this.single);
      }
      else {
        const invalid = this.list.filter(row => row.error);
        this.$('listResult').value = this.list.map(row => row.value ? value(row.value) : '').join('\n');
        this.$('listSummary').textContent = (this.list.length - invalid.length) + ' converted' + (invalid.length ? ' · ' + invalid.length + ' invalid' : '');
        this.querySelector('[data-uid-copy="list"]').disabled = this.$('download').disabled = invalid.length > 0;
        this.$('lineErrors').replaceChildren();
        for (const row of invalid.slice(0, 10)) { const li = document.createElement('li'); li.textContent = 'Line ' + row.line + ': ' + row.error; this.$('lineErrors').append(li); }
        if (invalid.length > 10) { const li = document.createElement('li'); li.textContent = (invalid.length - 10) + ' more invalid lines.'; this.$('lineErrors').append(li); }
      }
      this.$(kind + 'Results').hidden = false;
    }
    async copy(kind) {
      if (!this[kind] || (kind === 'list' && this.list.some(row => row.error))) return;
      const token = this.generation[kind], field = this.$(kind + 'Result'), text = field.value;
      try {
        if (!navigator.clipboard?.writeText) throw Error('Clipboard unavailable');
        await navigator.clipboard.writeText(text);
        if (token === this.generation[kind] && this.isConnected) this.status(kind, 'Copied.');
      } catch {
        if (token !== this.generation[kind] || !this.isConnected) return;
        field.focus(); field.select(); this.status(kind, 'Copy the selected result manually.');
      }
    }
    download() {
      if (!this.list || this.list.some(row => row.error)) return;
      const heading = 'uid_' + this.$('output').value + '_' + this.$('order').value + '_order';
      const csv = '\uFEFF' + [heading, ...this.$('listResult').value.split('\n')].map(value => '"' + value.replace(/"/g, '""') + '"').join('\r\n') + '\r\n';
      const url = URL.createObjectURL(new Blob([csv], {type: 'text/csv;charset=utf-8'}));
      const link = document.createElement('a'); link.href = url; link.download = 'converted-uids.csv'; document.body.append(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  }
  if (!customElements.get('nfc-uid-converter')) customElements.define('nfc-uid-converter', NfcUidConverter);

})();
